import { format } from "date-fns";
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";

import { Time } from "../../../utils/time";
import type { CalendarUiStateStore, TimeStep } from "../type";

export const CalendarUiStateContext = createContext<CalendarUiStateStore | null>(null);

export const CalendarUiStateProvider = ({
  date,
  startTimeStr = "08:00",
  endTimeStr = "22:00",
  children,
}: {
  date: Date;
  startTimeStr?: string;
  endTimeStr?: string;
  children: ReactNode;
}) => {
  const [timeStep, setTimeStep] = useState<TimeStep>(15);

  const startTime = useMemo(() => new Time(startTimeStr), [startTimeStr]);
  const endTime = useMemo(() => new Time(endTimeStr), [endTimeStr]);

  const cells = useMemo(
    () =>
      new Array(Math.floor(endTime.diff(startTime.minutes) / timeStep))
        .fill(undefined)
        .map((_, i) => startTime.add(timeStep * i).toDate(date)),
    [date, startTime, endTime, timeStep],
  );

  const cellsAddEnd = useMemo(() => [...cells, endTime.toDate(date)], [cells, endTime, date]);

  /**
   * 対象の時間が何番目のcellか計算
   */
  const computeCellIndex = useCallback(
    (target: Date) => {
      const targetTime = new Time(format(target, "HH:mm"));
      const startDiff = startTime.diff(targetTime.minutes);
      return Math.floor(startDiff / timeStep);
    },
    [startTime, timeStep],
  );

  /**
   * 対象のアイテムの時間がどの位置に入るかを特定
   */
  const computeItemPosition = useCallback(
    (target: Date) => new Time(format(target, "HH:mm")).diff(startTime.minutes) / timeStep,
    [startTime, timeStep],
  );

  const computeIsOutOfRange = useCallback(
    (target: Date) => {
      const time = new Time(format(target, "HH:mm"));
      return time.minutes > endTime.minutes || time.minutes < startTime.minutes;
    },
    [startTime, endTime],
  );

  return (
    <CalendarUiStateContext.Provider
      value={{
        date,
        cells,
        cellsAddEnd,
        timeStep,
        setTimeStep,
        computeCellIndex,
        computeItemPosition,
        computeIsOutOfRange,
      }}
    >
      {children}
    </CalendarUiStateContext.Provider>
  );
};

export const useCalendarUiStateStore = (): CalendarUiStateStore => {
  const context = useContext(CalendarUiStateContext);
  if (!context) {
    throw new Error("useCalendarUiStateStore must be used within a CalendarUiStateProvider");
  }
  return context;
};
